/**
 * An author's draft of a group screen — `docs/plans/author-screen-drafts.md`.
 *
 * ## Why a draft, and not the overlay or the screen
 *
 * In **Author as JP**, *Persons* (`org`, `grp-jp`) was treated as a club's
 * screen, because `isOwn` is `ownerKind === "user"`. So the author's edits went
 * to their **overlay**, which holds widths, order and hiding but never
 * `precision`, `format`, `colorBy` or `notation` — and a reopen refilled the
 * panel from base + overlay and dropped every format (owner, 2026-10-06).
 *
 * Writing the screen directly would put every half-made edit in front of every
 * member. So while authoring, the group's own screens are edited as a **draft**:
 *
 *     member            published screen  +  their overlay         (unchanged)
 *     author (as grp)   draft ?? published  ── Publish ──▶  published v(n+1)
 *                                          ── Discard ──▶  draft deleted
 *
 * The owner's decisions, 2026-10-06, all four load-bearing here:
 *
 * 1. **One draft per screen, shared** — the platform admin and the Group Admin
 *    see and publish the same one. Nothing here is per person but the
 *    "edited by" line.
 * 2. **A bar above the grid while a draft exists** (`components/DraftBar.tsx`).
 * 3. **The draft takes everything**, widths, order and hiding included. The
 *    author's overlay is not used for a screen they are authoring, so what is
 *    published is exactly what was on screen.
 * 4. Screens only.
 *
 * ## The same arrangement as `screen-sync`, with one difference
 *
 * Section first, server on the same 600ms debounce, nothing written until the
 * definition differs from what the draft was last known to hold — `useScreenSync`'s
 * shape, for its reasons. The difference is **where the baseline comes from**.
 * `useScreenSync` takes first sight of a screen as its baseline; that is wrong
 * here, because on mount the section holds whatever the author's panel had
 * before this existed, and the refill that follows would look like an edit — a
 * draft identical to the published screen, and a draft bar announcing changes
 * nobody made. So the baseline is **marked by the load** (`markBaseline`), and a
 * screen with no baseline writes nothing.
 *
 * ## Module scope
 *
 * The baselines, the pending writes and what is known about each draft are held
 * here rather than in a component — `lib/screens.ts`'s shape — because three
 * places in different subtrees act on one draft: the panel that writes it, the
 * bar that discards or publishes it, and the editor's Publish tab in `App`,
 * which must flush a pending write before it publishes.
 */

import { useEffect, useSyncExternalStore } from "react";
import type { ScreenerConfig } from "@qufolio/model";

import {
  discardDraft,
  fetchDraft,
  fetchPreset,
  publishDraft,
  publishPreset,
  republishPreset,
  saveDraft,
  type ScreenDraft,
  type ScreenSummary,
} from "../api/presets";
import { definitionOf, type Definition } from "./screen-sync";

/**
 * What a group screen's draft holds of the panel: the definition, and a Recs or
 * TopDown screen's own block. Without the block a TopDown authored as its group
 * reopened from its draft as an ordinary screen. Ticks never travel: they are
 * the reader's (`api/presets.ts`, `withoutTicks`), so ticking writes nothing.
 */
type DraftShape = Definition & {
  recs?: ScreenerConfig["recs"];
  topDown?: ScreenerConfig["topDown"];
  seasonality?: ScreenerConfig["seasonality"];
};

export function draftDefinitionOf(config: ScreenerConfig): DraftShape {
  const topDown = config.topDown ? (({ ticks: _t, ...rest }) => rest)(config.topDown) : undefined;
  return {
    ...definitionOf(config),
    ...(config.recs ? { recs: config.recs } : {}),
    ...(topDown ? { topDown } : {}),
    ...(config.seasonality ? { seasonality: config.seasonality } : {}),
  };
}
import { isAuthored, markDraft, markPublished, screenFor, type Screen } from "./screens";

/**
 * How long after the last edit the draft is written — `SYNC_DELAY_MS`'s 600, the
 * workspace's. A copy rather than an import only because test files that mock
 * `./screen-sync` whole would make the import a throw on every render of a
 * panel that is not authoring; `screen-draft.test.ts` holds the two equal.
 */
export const DRAFT_DELAY_MS = 600;

/** What the bar says about a draft — who touched it last, and when. */
export interface DraftMeta {
  updatedBy: string;
  updatedByName: string | null;
  updatedAt: string;
}

// ── the store ───────────────────────────────────────────────────────────────

/** Per screen key: the draft's meta, or absent when there is none. */
let metas = new Map<string, DraftMeta>();
/** Per screen key: the definition the draft (or published screen) holds. */
let baselines = new Map<string, string>();
/** Per screen key: a debounced write not yet sent, runnable now. */
let pending = new Map<string, () => Promise<void>>();
/** Per screen key: a write on the wire, so a flush can wait for it. */
let inFlight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** A baseline that matches no definition. `JSON.stringify` never returns "". */
const UNKNOWN = "";

function metaOf(draft: ScreenDraft): DraftMeta {
  return {
    updatedBy: draft.updatedBy,
    updatedByName: draft.updatedByName,
    updatedAt: draft.updatedAt,
  };
}

/** Record what is known about one screen's draft, and the tab's ✎ with it. */
export function setDraftMeta(key: string, meta: DraftMeta | null): void {
  const next = new Map(metas);
  if (meta) next.set(key, meta);
  else next.delete(key);
  metas = next;
  markDraft(key, meta !== null);
  notify();
}

export function draftMeta(key: string | undefined): DraftMeta | undefined {
  return key ? metas.get(key) : undefined;
}

/** The draft meta for one screen, as a hook. Undefined is "no draft". */
export function useDraftMeta(key: string | undefined): DraftMeta | undefined {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => draftMeta(key),
  );
}

/**
 * The definition this screen's server copy now holds, as the panel will show it.
 *
 * Called by whatever filled the panel, with the config *as it will be* after the
 * fill. Also drops a write still waiting: the panel was just replaced, and the
 * edit that write carried is not on screen any more — Discard is the case.
 */
export function markBaseline(key: string, config: ScreenerConfig): void {
  baselines.set(key, JSON.stringify(draftDefinitionOf(config)));
  pending.delete(key);
}

/**
 * Forget what this screen was known to hold, so nothing is written for it until
 * the next load marks a baseline again.
 *
 * Called the moment the panel starts loading an authored screen. Between a tab
 * click and the load answering, the panel can hold anything — the published
 * screen the tab strip filled it with, or the section's stale copy on a page
 * load — and none of that is an edit.
 */
export function forgetBaseline(key: string): void {
  baselines.delete(key);
  pending.delete(key);
}

/**
 * Send a pending draft write now, and wait for any write already on the wire.
 *
 * What Publish calls first: the author's last change may still be inside the
 * 600ms debounce, and publishing without it would publish the screen as it was
 * one edit ago while the panel showed the edit.
 */
export async function flushDraft(key: string): Promise<void> {
  const run = pending.get(key);
  if (run) await run();
  const flying = inFlight.get(key);
  if (flying) await flying;
}

/** Test seam: forget every draft, baseline and pending write. */
export function resetDrafts(): void {
  metas = new Map();
  baselines = new Map();
  pending = new Map();
  inFlight = new Map();
  notify();
}

// ── reading ─────────────────────────────────────────────────────────────────

/** What `applyScreenTo` fills a panel from. */
type Loaded = {
  columns?: ScreenerConfig["columns"];
  filter?: ScreenerConfig["filter"];
  sort?: ScreenerConfig["sort"];
  savedFilters?: ScreenerConfig["savedFilters"] | null;
  savedFilterCategoryOrder?: ScreenerConfig["savedFilterCategoryOrder"] | null;
  recs?: ScreenerConfig["recs"] | null;
  topDown?: ScreenerConfig["topDown"] | null;
  seasonality?: ScreenerConfig["seasonality"] | null;
};

/**
 * An authored screen's definition: **the draft if there is one, else the
 * published screen** — whole, formats included, and never through the overlay.
 *
 * Throws when the draft cannot be read (anything but 404). Falling back to the
 * published screen there would make the next edit overwrite the real draft.
 */
export async function loadAuthored(key: string): Promise<Loaded | null> {
  const draft = await fetchDraft(key);
  setDraftMeta(key, draft ? metaOf(draft) : null);
  if (draft) {
    return {
      columns: draft.columns,
      filter: draft.filter,
      sort: draft.sort,
      savedFilters: draft.savedFilters,
      savedFilterCategoryOrder: draft.savedFilterCategoryOrder,
      recs: draft.recs,
      topDown: draft.topDown,
      seasonality: draft.seasonality,
    };
  }
  return fetchPreset(key);
}

/**
 * The loader a tab strip should fill the panel with: draft-aware for a screen
 * this account is authoring, the published screen for everything else.
 *
 * The tab strip in the editor fills the panel before `ScreenerView` does. Given
 * the published screen while a draft exists, reselecting the tab already open
 * would put the published definition on screen — and the draft sync would then
 * write it over the draft.
 */
export function screenLoader(publishTo: string | null | undefined) {
  return (key: string): Promise<Loaded | null> => {
    const screen = screenFor(key);
    if (screen && isAuthored(screen, publishTo)) return loadAuthored(key).catch(() => null);
    return fetchPreset(key);
  };
}

// ── writing ─────────────────────────────────────────────────────────────────

/** Write the draft and record that one now exists — the bar appears on this. */
export async function writeDraft(key: string, definition: DraftShape): Promise<void> {
  const saved = await saveDraft(key, definition);
  setDraftMeta(key, metaOf(saved));
}

/** Throw the draft away. The caller refills the panel from the published screen. */
export async function discardAuthored(key: string): Promise<void> {
  pending.delete(key);
  await discardDraft(key);
  setDraftMeta(key, null);
}

/**
 * Publish the draft **to the screen's own key** — flushed first — and end it.
 *
 * Nothing on the panel changes: what is published is what it shows, so the
 * baseline stands and the next edit opens a new draft.
 */
export async function publishAuthored(key: string): Promise<ScreenSummary> {
  await flushDraft(key);
  if (!metas.has(key)) throw new Error("There are no unpublished changes to publish.");
  const published = await publishDraft(key);
  setDraftMeta(key, null);
  markPublished(key, published.version);
  return published;
}

/**
 * The editor's Publish tab: **an existing group screen is republished under its
 * own key**, and only a brand-new name is slugged.
 *
 * `publishPreset` slugs `ownerKey-name`, so publishing *Persons* wrote
 * `grp-jp-persons` beside `jp-persons` (`docs/plans/author-screen-drafts.md`,
 * step 5). "Exists" is a screen of this group with that name, read from the
 * listing — the same thing the tab's own text promises: "publishing under a name
 * already published replaces it". The one on the panel goes through its draft,
 * so what is published is exactly what was on screen; another one by name takes
 * the panel's columns under *its* key.
 */
export async function publishFromEditor(input: {
  name: string;
  description: string;
  columns: ScreenerConfig["columns"];
  filter: ScreenerConfig["filter"];
  sort: ScreenerConfig["sort"];
  recs?: ScreenerConfig["recs"];
  topDown?: ScreenerConfig["topDown"];
  seasonality?: ScreenerConfig["seasonality"];
  publishTo: string;
  screens: readonly Screen[];
  /** The panel's `screenRef`. */
  showing: string | undefined;
}): Promise<void> {
  const { publishTo, screens, showing, ...definition } = input;
  const existing = screens.find(
    (s) => isAuthored(s, publishTo) && s.name.trim() === definition.name.trim(),
  );
  if (existing && existing.key === showing) {
    await publishAuthored(existing.key);
    return;
  }
  if (existing) {
    await republishPreset({
      ...definition,
      key: existing.key,
      ownerKey: publishTo,
      market: existing.market,
      groupCodes: existing.groupCodes,
    });
    return;
  }
  await publishPreset({ ...definition, ownerKey: publishTo });
}

// ── the sync ────────────────────────────────────────────────────────────────

export interface DraftSyncOptions {
  /** The panel, as it stands now. */
  config: ScreenerConfig;
  /** Whether the screen on the panel is one this account is authoring. */
  authored: boolean;
  /** Writes the draft. Injected so this is testable without a server. */
  save?: (key: string, definition: DraftShape) => Promise<unknown>;
  delay?: number;
}

/**
 * Push the panel's definition to its screen's draft, after it settles.
 *
 * `useScreenSync`'s debounce and its failure rule — a failed write marks the
 * baseline unknown, so the next edit resends the whole thing — with the
 * baseline taken from the load rather than from first sight (see the header).
 *
 * **A write still waiting when the panel unmounts is sent, not dropped.**
 * Closing the screener inside the debounce otherwise loses the last edit from
 * the draft, and the next open refills the section from the draft — so unlike
 * a user's own screen, where the section keeps it, here it would be gone.
 */
export function useDraftSync({
  config,
  authored,
  save = writeDraft,
  delay = DRAFT_DELAY_MS,
}: DraftSyncOptions): void {
  const key = config.screenRef;
  const encoded = JSON.stringify(draftDefinitionOf(config));

  useEffect(() => {
    if (!key || !authored) return;
    const held = baselines.get(key);
    // No baseline: the load has not answered, so nothing on the panel is an edit.
    if (held === undefined) return;
    if (held === encoded) {
      pending.delete(key);
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    const run = async (): Promise<void> => {
      if (timer) clearTimeout(timer);
      timer = null;
      if (pending.get(key) === run) pending.delete(key);
      baselines.set(key, encoded);
      const write = Promise.resolve()
        .then(() => save(key, JSON.parse(encoded) as DraftShape))
        .then(
          () => undefined,
          () => {
            baselines.set(key, UNKNOWN);
          },
        );
      inFlight.set(key, write);
      await write;
      if (inFlight.get(key) === write) inFlight.delete(key);
    };
    pending.set(key, run);
    timer = setTimeout(() => void run(), delay);

    return () => {
      // Superseded by the next edit, which registers its own; or unmounting,
      // which `flushOnUnmount` below sends. Only the timer is ours to stop.
      if (timer) clearTimeout(timer);
    };
  }, [key, authored, encoded, delay, save]);

  useEffect(
    () => () => {
      if (key) void flushDraft(key);
    },
    [key],
  );
}
